// A/B Testing Framework Type Definitions
import type { ModelRouteId } from '../llm-routing'

// Stage status tracking
export type StageStatus = 'idle' | 'running' | 'completed' | 'error'

// Email providers
export type EmailProvider = 'gmail' | 'outlook' | 'both'

// LLM Providers
export type LLMProvider = 'openai' | 'claude' | 'gemini' | 'groq' | 'deepinfra' | 'xai' | 'ollama' | 'webllm'

// IAB Classification sections
export type IABSection = 'demographics' | 'household' | 'interests' | 'purchase_intent'

// Core Email interface
export interface Email {
  id: string
  subject: string
  from: string
  body: string
  date: string  // ISO date string
}

// Pre-processed email with summary
export interface PreprocessedEmail extends Email {
  summary: string
  summaryTokenCount: number
  processedContent?: string  // For full classifier - may contain sanitized content
}

// Evidence quality type from Evidence Judge (v13 Section 5.3)
export type EvidenceType = 'explicit' | 'contextual' | 'weak' | 'inappropriate'

// Model pricing (per 1M tokens)
export interface ModelPricing {
  inputPer1M: number
  outputPer1M: number
}

// Model configuration
export interface ModelConfig {
  provider: LLMProvider
  model: string
  displayName: string
  pricing?: ModelPricing | null
}

// Individual classification result
export interface Classification {
  emailId: string
  category: string
  taxonomyId: string
  confidence: number
  reasoning: string
  section: IABSection
  // NEW: Evidence quality from Evidence Judge (Sprint 11d)
  evidenceQuality?: number           // 0.0-1.0 score from judge
  evidenceType?: EvidenceType        // explicit | contextual | weak | inappropriate
  // NEW: Tier path for granularity analysis
  tierPath?: string                  // Full path: "Tier1 > Tier2 > Tier3"
  tier1?: string
  tier2?: string
  tier3?: string
  tier4?: string
  tier5?: string
  // Grouping for mutually-exclusive categories
  groupingValue?: string             // e.g., "Gender", "Age", "Interest Category"
}

// Results for a single model
export interface ModelResults {
  modelKey: string  // "provider:model"
  classifications: Classification[]
  stats: {
    avgConfidence: number
    minConfidence: number
    maxConfidence: number
    totalClassifications: number
    uniqueCategories: string[]
    // NEW: Evidence quality stats (Sprint 11d)
    avgEvidenceQuality?: number
    evidenceTypeDistribution?: Record<EvidenceType, number>
  }
  timing?: {
    startTime: string  // ISO date string
    endTime: string    // ISO date string
    durationMs: number
  }
  // NEW: Processing time in ms (Sprint 11d)
  processingTime?: number
  // NEW: Workflow metadata (Sprint 11d)
  errors?: string[]
  warnings?: string[]
  reconciliationData?: Array<Record<string, unknown>>
  // NEW: Cost tracking (Sprint 11d, v13 Section 6.10)
  cost?: {
    inputTokens: number
    outputTokens: number
    totalCost: number  // USD
  }
}

// Stage 1 export format
export interface Stage1Export {
  version: '1.0'
  exportedAt: string
  downloadConfig: {
    provider: EmailProvider
    maxEmails: number
    userId: string
  }
  emails: Email[]
}

// Stage 2 export format
export interface Stage2Export {
  version: '1.0'
  exportedAt: string
  preprocessConfig: {
    routeId?: ModelRouteId
    summarizerProvider: string
    summarizerModel: string
    sourceStage1File?: string
  }
  emails: PreprocessedEmail[]
}

// Stage 3 export format
export interface Stage3Export {
  version: '1.0'
  exportedAt: string
  sourceStage2File?: string
  models: ModelConfig[]
  results: Record<string, ModelResults>
  comparisonMetrics: ComparisonMetrics
}

// Per-model statistics
export interface ModelStats {
  avgConfidence: number
  minConfidence: number
  maxConfidence: number
  stdDevConfidence: number
  totalClassifications: number
  uniqueCategories: string[]
  classificationRate: number
  durationMs: number
  // NEW: Evidence quality stats (Sprint 11d)
  avgEvidenceQuality?: number
  evidenceTypeDistribution?: Record<EvidenceType, number>
  blockedCount?: number  // Classifications blocked by evidence judge (<0.15)
  // NEW: Cost tracking (Sprint 11d)
  cost?: {
    inputTokens: number
    outputTokens: number
    totalCost: number
  }
}

// Cross-model agreement metrics
export interface AgreementMetrics {
  fullAgreementCount: number
  partialAgreementCount: number
  noAgreementCount: number
  agreementRate: number
  pairwiseAgreement: Record<string, Record<string, number>>
}

// Category coverage metrics
export interface CoverageMetrics {
  categoriesByModel: Record<string, string[]>
  commonCategories: string[]
  uniqueCategories: Record<string, string[]>
  categoryFrequency: Record<string, number>
}

// Complete comparison metrics
export interface ComparisonMetrics {
  modelStats: Record<string, ModelStats>
  agreement: AgreementMetrics
  coverage: CoverageMetrics
}

// Main page state
export interface ABTestingState {
  // Stage tracking
  currentStage: 1 | 2 | 3
  stageStatus: {
    download: StageStatus
    preprocess: StageStatus
    classify: StageStatus
  }

  // Stage 1: Downloaded emails
  downloadedEmails: Email[]
  downloadConfig: {
    provider: EmailProvider
    maxEmails: number
  }

  // Stage 2: Pre-processed emails
  preprocessedEmails: PreprocessedEmail[]
  preprocessConfig: {
    routeId?: ModelRouteId
    summarizerModel: string
    summarizerProvider: string
  }

  // Stage 3: Classification results
  selectedModels: ModelConfig[]
  classificationResults: Map<string, ModelResults>

  // Comparison metrics
  comparisonMetrics: ComparisonMetrics | null
}

/**
 * Fallback models for selection (used when API is not available)
 *
 * @deprecated Bundled fallback - for dynamic fallbacks use:
 * ```typescript
 * import { configService } from '@ownyou/llm-client';
 * const fallbackModels = await configService.getFallbackModels();
 * ```
 *
 * @see docs/architecture/extracts/llm-cost-6.10.md
 */
export const FALLBACK_MODELS: ModelConfig[] = [
  // OpenAI
  { provider: 'openai', model: 'gpt-4o-mini', displayName: 'GPT-4o-mini' },
  { provider: 'openai', model: 'gpt-4o', displayName: 'GPT-4o' },
  // Claude
  { provider: 'claude', model: 'claude-3-5-sonnet-20241022', displayName: 'Claude 3.5 Sonnet' },
  { provider: 'claude', model: 'claude-3-5-haiku-20241022', displayName: 'Claude 3.5 Haiku' },
  // Gemini
  { provider: 'gemini', model: 'gemini-1.5-flash', displayName: 'Gemini 1.5 Flash' },
  { provider: 'gemini', model: 'gemini-2.0-flash-exp', displayName: 'Gemini 2.0 Flash' },
  // Groq
  { provider: 'groq', model: 'llama-3.3-70b-versatile', displayName: 'Llama 3.3 70B (Groq)' },
  // DeepInfra
  { provider: 'deepinfra', model: 'meta-llama/Llama-3.3-70B-Instruct', displayName: 'Llama 3.3 70B (DeepInfra)' },
]

/**
 * @deprecated Use configService.getFallbackModels() instead.
 * This alias exists for backwards compatibility.
 */
export const AVAILABLE_MODELS = FALLBACK_MODELS

// Re-export fetchAvailableModels from the ConfigService-based implementation
// This uses dynamic pricing and model data from llm-prices.com/OpenRouter
export { fetchAvailableModels } from '../../utils/fetch-models'
